import { useState, type ReactNode } from 'react';
import { useLocation } from 'react-router-dom';
import { StateFrame } from '@/components/layout/workspace/StateFrame';
import { VIEW_TAB } from '@/components/layout/workspace/types';
import { usePane, useTabId, useWorkspace } from '@/components/layout/workspace/workspaceContext';
import { focusedPath } from '@/components/layout/workspace/workspaceReducer';
import { APP_OPEN_FAILURE } from '../appOpen';
import { usePrefs } from '../prefs';
import { useShellCommands } from '../shellCommands';
import { CrashProbe, NoAccessScreen, NotFoundScreen } from './StateScreens';

/*
  StateGate — обёртка видов «Основы» для образцов ожидания и ошибок (R42):
  - переключатель «Вкладка» на верхней полосе действует на активную вкладку окна в фокусе, какой бы вид в
    ней ни был: вместо вида — «не найдено» или «нет доступа». Сам вид при этом не размонтируется, а
    прячется: прокрутка и ввод живут (R37) и возвращаются, как были, когда образец снят;
  - «Уронить вкладку» взводит пробу в той же вкладке — ошибку ловит настоящая граница вкладки;
  - у вкладок приложения (app) — рамка холодного открытия: вкладка, вставшая, пока приложение впервые за сеанс
    открывается, вместо вида показывает медальон по шагам. Рамка стоит всегда, даже когда открывать нечего, —
    иначе вид перемонтировался бы, когда открытие кончится.
*/

export function StateGate({ children, app = false }: { children: ReactNode; app?: boolean }) {
  const { tabDemo, crashAt } = usePrefs();
  const ws = useWorkspace();
  // Адрес вкладки: у каждой вкладки своё дерево маршрутов со своим адресом.
  const { pathname } = useLocation();
  const demo = tabDemo !== 'as-is' && pathname === focusedPath(ws.state) ? tabDemo : null;
  const r = ws.resolve(pathname);
  return (
    <>
      <CrashProbe armed={crashAt === pathname} />
      <div hidden={demo !== null} className="contents">
        {app ? <ColdOpenFrame service={r.serviceId}>{children}</ColdOpenFrame> : children}
      </div>
      {demo === 'missing' && <NotFoundScreen />}
      {demo === 'locked' && <NoAccessScreen key={pathname} name={r.title} object={r.type === 'object'} />}
    </>
  );
}

/**
 * Холодное открытие во вкладке приложения (spec-2 §3.2): первые 400 мс пусто, потом медальон по шагам; на
 * «готово» веер досаживается и входит вид. Сбой на шаге: кольцо краснеет, «Повторить» — шаги идут дальше,
 * «Назад» — туда, откуда пришли. Дольше 10 с — «Повторить» и «Закрыть вкладку» (у вкладки вида — «Назад»).
 *
 * Холодная рамка — свойство вкладки, а не фокуса: ждёт только та, что встала во время открытия своего
 * приложения, и только до его конца. Вид, который уже на экране, следующие открытия не трогают (R37): окно
 * получило фокус, сменили темп, повторили первую загрузку — вкладка остаётся, как была.
 */
function ColdOpenFrame({ service, children }: { service: string; children: ReactNode }) {
  const { opening } = useShellCommands();
  const ws = useWorkspace();
  const pane = usePane();
  const tab = useTabId();
  const [cold, setCold] = useState(() => opening?.service === service);
  const live = cold && opening?.service === service ? opening : null;
  // Открытие кончилось — медальон ещё досаживает веер и уходит: держим его имя и шаги, все пройдены. Пропадает
  // открытие, только когда кончилось: без фокуса оболочка его не бросает (бросает «Назад», и вкладка уходит).
  // Дальше рамка своё отслужила.
  const [last, setLast] = useState(live);
  if (live && live !== last) setLast(live);
  if (cold && last && !live) setCold(false);
  const run = live ?? (last && { ...last, done: last.steps.length, failed: false });
  const leave =
    tab !== null && tab !== VIEW_TAB
      ? { label: 'Закрыть вкладку', onClick: () => ws.close(pane, tab) }
      : { label: 'Назад', onClick: run?.back };
  return (
    <StateFrame
      kind={run?.failed ? 'failed' : 'loading'}
      title={run?.failed ? `Не удалось открыть «${run.name}»` : `Открываем «${run?.name ?? ''}»`}
      steps={run?.steps ?? []}
      done={run?.done ?? 0}
      failed={run?.failed ? { step: run.steps[run.done], reason: APP_OPEN_FAILURE.reason } : undefined}
      action={
        run?.failed
          ? { label: 'Повторить', variant: 'primary', onClick: run.retry }
          : { label: 'Повторить', onClick: run?.retry }
      }
      secondaryAction={run?.failed ? { label: 'Назад', onClick: run.back } : leave}
      details={run?.failed ? APP_OPEN_FAILURE.details : undefined}
    >
      {children}
    </StateFrame>
  );
}
